import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utcPlugin);
dayjs.extend(customParseFormat);

type LocaleInfo = {
  startTime: string;
  endTime: string;
  weekdayTime: string;
  timezoneNote: string;
  placeholderTime: string;
  placeholderTimezone: string;
  customPlaceholder?: string;
};

type SupportedLocales = {
  [key: string]: LocaleInfo;
};

export const LOCALE_REGION_MAPPING: { [locale: string]: string } = {
  'en-au': 'APGCEnglish',
  'en-ca': 'default',
  'da-dk': 'EMEAEnglish',
  'en-in': 'APGCEnglish',
  'id-id': 'APGCEnglish',
  'en-ie': 'EMEAEnglish',
  'en-my': 'APGCEnglish',
  'nl-nl': 'EMEAEnglish',
  'en-nz': 'APGCEnglish',
  'nb-no': 'EMEAEnglish',
  'en-ph': 'APGCEnglish',
  'en-sg': 'APGCEnglish',
  'sv-se': 'EMEAEnglish',
  'en-gb': 'EMEAEnglish',
  'en-us': 'default',
  'vi-vn': 'APGCEnglish',
  'zh-cn': 'APGCEnglish',
  'zh-hans': 'APGCEnglish',
  'fr-ca': 'EMEAFrench',
  'fr-fr': 'EMEAFrench',
  'de-at': 'EMEAGerman',
  'de-de': 'EMEAGerman',
  'de-ch': 'EMEAGerman',
  'it-it': 'EMEAItalian',
  'ja-jp': 'APGCJapanese',
  'pt-br': 'LATAMPortuguese',
  'es-ar': 'LATAMSpanish',
  'es-cl': 'LATAMSpanish',
  'es-co': 'LATAMSpanish',
  'es-mx': 'LATAMSpanish',
  'es-pe': 'LATAMSpanish',
  'es-es': 'EMEASpanish',
  'es-ve': 'LATAMSpanish',
};

export const LIVE_AGENT_SUPPORTED_LOCALES = [
  'en-au',
  'en-ca',
  'da-dk',
  'en-in',
  'id-id',
  'en-ie',
  'en-my',
  'en-nz',
  'nb-no',
  'en-ph',
  'en-sg',
  'sv-se',
  'en-gb',
  'en-us',
  'vi-vn',
  'fr-ca',
  'fr-fr',
  'de-at',
  'de-de',
  'de-ch',
  'it-it',
  'nl-nl',
  'pt-br',
  'es-ar',
  'es-cl',
  'es-co',
  'es-mx',
  'es-pe',
  'es-es',
  'es-ve',
];

export const LIVE_AGENT_LIMITED_SUPPORT_STRINGS: Record<string, string> = {
  // These strings are now coommon for all limited support locales
  header: _TL_('We\'re sorry-the live chat is closed.'),
  hours: _TL_('Hours:'),
  weekendCloseNote: _TL_('Sat-Sun: Closed'),
  footer: _TL_('Copilot in the Microsoft Advertising Platform is available all day. Please feel free to ask me a question to receive quick assistance.'),
};

export const LIVE_AGENT_LIMITED_SUPPORT_LOCALES: SupportedLocales = {
  'ja-jp': {
    startTime: '0:00',
    endTime: '8:30',
    weekdayTime: _TL_('Mon-Fri: 9:00 AM-5:30 PM'),
    timezoneNote: _TL_('*Note:* These hours are in the Japan Standard Time zone.'),
  },
};

export type HolidayCloseHoursLocaleMap = {
  [locale: string]: {
    [date: string]: {
      utcEndTime: string;
    } | 'closed';
  };
};

export const LIVE_AGENT_HOLIDAY_CLOSE_HOURS_LOCALE_MAP: HolidayCloseHoursLocaleMap = {
  'it-it': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-06': 'closed',
  },
  'ja-jp': {
    '2026-01-01': 'closed',
    '2026-01-12': 'closed',
    '2026-02-11': 'closed',
    '2026-02-23': 'closed',
    '2026-03-20': 'closed',
    '2026-04-29': 'closed',
    '2026-05-03': 'closed',
    '2026-05-04': 'closed',
    '2026-05-05': 'closed',
    '2026-05-06': 'closed',
    '2026-07-20': 'closed',
    '2026-08-11': 'closed',
    '2026-09-21': 'closed',
    '2026-09-22': 'closed',
    '2026-09-23': 'closed',
    '2026-10-12': 'closed',
    '2026-11-03': 'closed',
    '2026-11-23': 'closed',
  },
  'nl-nl': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'fr-fr': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-06': 'closed',
  },
  'fr-ca': {
    '2025-04-21': 'closed',
    '2026-04-06': 'closed',
  },
  'de-ch': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'de-at': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'de-de': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'de-sz': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'es-es': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '12:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-co': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-mx': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-ar': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-cl': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2025-04-18': 'closed',
    '2026-04-03': 'closed',
  },
  'es-pe': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-ve': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'pt-br': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '15:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
};

export const LIVE_AGENT_OPTIMIZATION_QUEUE_HOLIDAY_CLOSE_HOURS_LOCALE_MAP: HolidayCloseHoursLocaleMap = {
  'it-it': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-06': 'closed',
  },
  'ja-jp': {
    '2026-01-01': 'closed',
    '2026-01-12': 'closed',
    '2026-02-11': 'closed',
    '2026-02-23': 'closed',
    '2026-03-20': 'closed',
    '2026-04-29': 'closed',
    '2026-05-03': 'closed',
    '2026-05-04': 'closed',
    '2026-05-05': 'closed',
    '2026-05-06': 'closed',
    '2026-07-20': 'closed',
    '2026-08-11': 'closed',
    '2026-09-21': 'closed',
    '2026-09-22': 'closed',
    '2026-09-23': 'closed',
    '2026-10-12': 'closed',
    '2026-11-03': 'closed',
    '2026-11-23': 'closed',
  },
  'nl-nl': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'fr-fr': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-06': 'closed',
  },
  'fr-ca': {
    '2025-04-21': 'closed',
    '2026-04-06': 'closed',
  },
  'de-ch': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'de-at': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'de-de': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'de-sz': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
    '2026-04-06': 'closed',
  },
  'es-es': {
    '2025-12-24': { utcEndTime: '12:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '09:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-co': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-mx': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-ar': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-cl': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-pe': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'es-ve': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '16:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'pt-br': {
    '2025-12-24': { utcEndTime: '16:00' },
    '2025-12-25': 'closed',
    '2025-12-26': 'closed',
    '2025-12-31': { utcEndTime: '15:00' },
    '2026-01-01': 'closed',
    '2026-04-03': 'closed',
  },
  'en-au': {
    '2026-01-01': 'closed',
  },
  'en-ca': {
    '2026-01-01': 'closed',
  },
  'da-dk': {
    '2026-01-01': 'closed',
  },
  'en-in': {
    '2026-01-01': 'closed',
  },
  'id-id': {
    '2026-01-01': 'closed',
  },
  'en-ie': {
    '2026-01-01': 'closed',
  },
  'en-my': {
    '2026-01-01': 'closed',
  },
  'en-nz': {
    '2026-01-01': 'closed',
  },
  'nb-no': {
    '2026-01-01': 'closed',
  },
  'en-ph': {
    '2026-01-01': 'closed',
  },
  'en-sg': {
    '2026-01-01': 'closed',
  },
  'sv-se': {
    '2026-01-01': 'closed',
  },
  'en-gb': {
    '2026-01-01': 'closed',
  },
  'en-us': {
    '2026-01-01': 'closed',
  },
  'vi-vn': {
    '2026-01-01': 'closed',
  },
};

export const isCustomerSupportClosedForHoliday = (
  locale: string,
  holidayCloseHoursLocaleMap: HolidayCloseHoursLocaleMap
): boolean => {
  const currentDateTime = dayjs.utc();
  const currentDate = currentDateTime.format('YYYY-MM-DD');
  const endTime = holidayCloseHoursLocaleMap[locale]?.[currentDate];

  // if the date is not in the map, then the support is available
  if (endTime == null) return false;

  // if the date is in the map and the value is 'closed', then the support is closed
  if (endTime === 'closed') return true;

  const { utcEndTime } = endTime;
  // if the date is in the map and current time is beyond the working hours, then the support is closed
  const formattedEndTime = dayjs.utc(utcEndTime, 'HH:mm');
  return currentDateTime.isAfter(formattedEndTime);
};

const DST_ENABLED_LOCALES = new Set([
  'en-au', 'en-ca', 'da-dk', 'en-ie', 'nl-nl', 'en-nz', 'nb-no',
  'sv-se', 'en-gb', 'en-us', 'fr-ca', 'fr-fr', 'de-at', 'de-de',
  'de-ch', 'it-it', 'es-es',
]);

/**
 * `isDSTEnabled` checks if Daylight Saving Time (DST) is enabled for the given locale.
 * @param {String} locale Date for which DST needs to be checked. Default is current date.
 * @returns {Boolean} `true` if DST is enabled, `false` otherwise.
 */
export const isDSTEnabled = (locale: string): boolean => {
  if (!locale) return false;
  return DST_ENABLED_LOCALES.has(locale.toLowerCase());
};

/**
 * `updateTimeRangeForDST` updates the start and end time for the given date if DST is enabled.
 * It subtracts 1 hour from the start and end time if DST is enabled.
 * @param {Object} startTime Start time in HH:mm format.
 * @param {Object} endTime End time in HH:mm format.
 * @returns {Object} Updated start and end time.
 */
export const updateTimeRangeForDST = ({
  startTime,
  endTime,
  locale,
}: {
  startTime: string;
  endTime: string;
  locale: string;
}) => {
  const isDST = isDSTEnabled(locale);
  if (!isDST) {
    return { startTime, endTime };
  }

  return {
    startTime: dayjs(startTime, 'H:mm').subtract(1, 'hour').format('H:mm'),
    endTime: dayjs(endTime, 'H:mm').subtract(1, 'hour').format('H:mm'),
  };
};

// Check if the day is Monday to Friday
const isWorkingDay = (day: number) => day > 0 && day < 6;
const isWithinTimeRange = (currentTime: any, startTime: any, endTime: any) => (currentTime.isSame(startTime) || currentTime.isAfter(startTime))
  && currentTime.isBefore(endTime);

export const isCustomerSupportAvailable = (
  locale: string,
  startTime: string,
  endTime: string
): boolean => {
  const currentMoment = dayjs()
    .locale(locale)
    .utc(); // Convert to UTC
  const currentDay = currentMoment.day(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday

  // Check if it's a working day
  if (!isWorkingDay(currentDay)) {
    return false;
  }

  // Check if it's within any support time range
  // We offset the UTC difference by adding current day, since it gets reset to original date on enforcing utc
  const startTimeInUTC = dayjs.utc(startTime, 'HH:mm').day(currentDay);
  const endTimeInUTC = dayjs.utc(endTime, 'HH:mm').day(currentDay);

  // Handle the case where end time is on the next day
  if (endTimeInUTC.isBefore(startTimeInUTC)) {
    const nextDayEndTime = endTimeInUTC.clone().add(1, 'day');
    if (
      isWithinTimeRange(currentMoment, startTimeInUTC, nextDayEndTime)
      || isWithinTimeRange(
        currentMoment,
        startTimeInUTC.subtract(1, 'day'),
        endTimeInUTC
      )
    ) {
      return true;
    }
  } else if (isWithinTimeRange(currentMoment, startTimeInUTC, endTimeInUTC)) {
    return true;
  }

  return false;
};

export const isLiveChatSupportOpen = (locale: string | undefined): boolean => {
  if (!locale) return false;

  const lowerLocale = locale.toLocaleLowerCase();

  // Holiday closures apply to every locale, including the 24/7 ones.
  if (isCustomerSupportClosedForHoliday(lowerLocale, LIVE_AGENT_HOLIDAY_CLOSE_HOURS_LOCALE_MAP)) {
    return false;
  }

  if (LIVE_AGENT_SUPPORTED_LOCALES.includes(lowerLocale)) {
    return true;
  }
  if (LIVE_AGENT_LIMITED_SUPPORT_LOCALES[lowerLocale]) {
    const localeInfo = LIVE_AGENT_LIMITED_SUPPORT_LOCALES[lowerLocale];
    const { startTime, endTime } = updateTimeRangeForDST({
      startTime: localeInfo.startTime,
      endTime: localeInfo.endTime,
      locale,
    });
    return isCustomerSupportAvailable(lowerLocale, startTime, endTime);
  }
  return false;
};

export const isLocaleSupported = (locale: string | undefined): boolean => {
  if (!locale) return false;

  if (
    LIVE_AGENT_SUPPORTED_LOCALES.includes(locale)
    || locale in LIVE_AGENT_LIMITED_SUPPORT_LOCALES
  ) {
    return true;
  }
  return false;
};

const ENGLISH_ONLY_SUPPORTED_LOCALES = [
  'hi-in', 'cs-cz', 'fi-fi', 'ko-kr', 'th-th',
  'el-gr', 'fil-ph', 'pl-pl', 'ro-ro', 'uk-ua',
  'nb-no', 'hu-hu', 'ms-my', 'ru-ru', 'tr-tr',
  'bg-bg', 'hr-hr', 'lt-lt', 'zh-cn', 'zh-hk', 'zh-tw', 'zh-hans', 'zh-hant',
];

export const isEnglishOnlyChatSupportedLocale = (locale?: string): boolean => {
  if (!locale) return false;

  const lowerLocale = locale.toLocaleLowerCase();
  return ENGLISH_ONLY_SUPPORTED_LOCALES.includes(lowerLocale);
};

const LOCALES_TO_SKIP_LIVE_CHAT_SUPPORT_CHECK: string[] = [];

export const shouldSkipLiveChatSupportOpenCheck = (
  locale?: string,
  skipLocaleList: string[] = LOCALES_TO_SKIP_LIVE_CHAT_SUPPORT_CHECK
): boolean => {
  if (!locale) return false;
  if (!skipLocaleList.length) return false;

  const skipLowerLocaleList = skipLocaleList.map(skipLocale => skipLocale.toLocaleLowerCase());
  const lowerLocale = locale.toLocaleLowerCase();
  return skipLowerLocaleList.includes(lowerLocale);
};
